import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { feedApi } from "@/api/feedApi"
import { FeedRecordForm } from "../components/FeedRecordForm"
import { FormPageLayout } from "@/shared/components/forms"
import { PageLoading } from "@/shared/components/ui/LoadingSpinner"
import { Card } from "@/shared/components/ui/Card"
import { Button } from "@/shared/components/ui/Button"
import { useFarmFromUrl } from "@/shared/hooks/useUrlSync"
import { pondsApi } from "@/features/ponds/api/pondsApi"
import { queryKeys } from "@/shared/api/queryClient"
import { buildRoute } from "@/shared/lib/constants"
import { toApiRequest, type FeedRecordFormData } from "../schemas/feedRecordSchema"

export function EditFeedRecordPage() {
  const { recordId } = useParams<{ recordId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()

  const { farmId, isLoading: isFarmLoading } = useFarmFromUrl()
  const pondId = searchParams.get("pondId")

  // Fetch pond data using query params
  const { data: pond, isLoading: isPondLoading } = useQuery({
    queryKey: queryKeys.ponds.detail(farmId || "", pondId || ""),
    queryFn: () => pondsApi.get(farmId!, pondId!),
    enabled: !!farmId && !!pondId,
  })

  // Fetch existing record data
  const { data: record, isLoading: isRecordLoading } = useQuery({
    queryKey: ["feed", farmId, pondId, recordId],
    queryFn: () => feedApi.getRecord(farmId!, pondId!, recordId!),
    enabled: !!farmId && !!pondId && !!recordId,
  })

  const updateMutation = useMutation({
    mutationFn: (data: FeedRecordFormData) => {
      if (!farmId || !pondId || !recordId) throw new Error("Farm, pond, and record required")
      return feedApi.update(farmId, pondId, recordId, toApiRequest(data))
    },
    onSuccess: () => {
      if (pondId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.feed.all(pondId) })
      }
      if (farmId && pondId && recordId) {
        queryClient.invalidateQueries({
          queryKey: ["feed", farmId, pondId, recordId],
        })
      }
      handleCancel()
    },
  })

  const handleCancel = () => {
    if (farmId && recordId) {
      navigate(buildRoute.feedDetail(farmId, recordId, pondId))
    } else if (farmId) {
      navigate(buildRoute.feed(farmId, pondId))
    }
  }

  const handleSubmit = async (data: FeedRecordFormData) => {
    await updateMutation.mutateAsync(data)
  }

  if (isFarmLoading || isPondLoading || isRecordLoading) {
    return <PageLoading message="Loading feed record..." />
  }

  if (!record) {
    return (
      <Card>
        <div className="text-center py-16">
          <h3 className="font-roboto text-base font-semibold text-gray-900">
            Record not found
          </h3>
          <p className="font-roboto text-sm text-gray-600 mt-2">
            The feed record you're trying to edit doesn't exist.
          </p>
          <Button onClick={handleCancel} className="mt-4">
            Back to Feed Records
          </Button>
        </div>
      </Card>
    )
  }

  // Convert API response to form default values
  const defaultValues: Partial<FeedRecordFormData> = {
    feedType: record.feedType || "",
    feedBrand: record.feedBrand || "",
    totalFeed: record.totalFeed,
    maximumFeed: record.maximumFeed,
    appetite: record.appetite,
    feedTime: new Date(record.feedTime),
    notes: record.notes ?? "",
    imageIds: [],
  }

  return (
    <FormPageLayout
      title="Edit Feed Record"
      subtitle={pond?.name}
      onCancel={handleCancel}
      onSubmit={() => {
        document.getElementById("feed-record-form")?.dispatchEvent(
          new Event("submit", { cancelable: true, bubbles: true })
        )
      }}
      isSubmitting={updateMutation.isPending}
      submitLabel="Save Changes"
      cancelLabel="Cancel"
    >
      <FeedRecordForm
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        isLoading={updateMutation.isPending}
      />
    </FormPageLayout>
  )
}
